import { appPoolNodeGroupGpuSchema, appPoolNodeGroupId, appPoolNodeGroupSpecSchema, isAppPoolNodeGroupTierKey, machineTypeFamilyOnCloud, tierFitsGroupMachine } from "./app-pool-node-group.js";
import { AppPoolGrantSchema, assertAppPoolGrantConsistent } from "./desired-state.js";
import { APP_POOL_SATELLITE_MEMBERS_MAX } from "./status-report.schemas.js";
import { APP_ID_UUID_PATTERN, APP_POOL_CONCURRENCY_MAX, APP_POOL_LABEL_VALUE_PATTERN, APP_POOL_MAX_NODES_MAX, APP_POOL_NODE_GROUP_ID_PATTERN, APP_POOL_NODE_TYPE_MAX_LENGTH, APP_POOL_PLACEMENT_STATES, APP_POOL_REGION_PATTERN, APP_POOL_WARM_FLOOR_MAX, MAX_GPU_COUNT, MAX_WORK_ERROR_CHARS, MAX_WORK_LOG_CHUNK_CHARS, WORK_REASONS, acceleratorTypesFor, appPoolImagePullRefSchema, appPoolNameSchema, appPoolNodeIdSchema, appPoolOrgIdSchema, appPoolOutOfMeshSchema, appPoolRegionSchema, appPoolRunFrameSchema, appPoolTierKey, appVersionStringSchema, computeVectorSchema, isAppPoolGpuTier, isGpuComputeClass, workAttemptIdSchema, workResultSchema, workRunIdSchema } from "@zion/sdk-contracts";
import { z } from "zod";
//#region src/app-pool-subjects.ts
/**
* The satellite NATS wire (2026-09-29-gpu-and-regional-app-pools R-001, D-17, D-19, D-21): the five
* subjects a home operator and a satellite operator speak on, the request, reply, event and cancel
* shapes of a handed-off attempt, and the desired-state slice of the standing pools
* (2026-10-02-regional-pools-release-2 D-7).
*
* The home operator publishes one {@link appPoolSatelliteDesiredSliceSchema} message per enabled
* region on {@link appPoolSatelliteDesiredSubject} on every reconcile. The slice carries every
* standing out-of-mesh pool whose `region` is that region, resolved, with its membership, its
* resident orgs and its grant. The satellite applies a slice whose `generation` is greater than the
* one it holds and runs the members from it (DF-7). A lend of a standing member reuses
* {@link appPoolSatelliteRunSubject}: the request names the member set in `memberName`.
*
* The home dispatcher stays the only consumer of every pool subject and the only writer of
* `work.app_pool_runs`. For a regional pool it hands one attempt to the region's satellite operator
* as one NATS request on {@link appPoolSatelliteRunSubject}, carrying an immutable execution
* snapshot ({@link appPoolSatelliteRunRequestSchema}). The satellite answers the request at once with
* {@link appPoolSatelliteRunReplySchema}, builds the pod from the snapshot, and streams
* {@link appPoolSatelliteRunEventSchema} messages on {@link appPoolSatelliteRunEventsSubject}. It
* publishes its node census on {@link appPoolSatelliteStatusSubject}; the message shape is
* `SatelliteStatusMessageSchema` in `status-report.schemas.ts`, beside the `StatusReport` entry the
* home operator merges it into.
*
* The home dispatcher stops a handed-off attempt by publishing {@link appPoolSatelliteRunCancelSchema}
* on {@link appPoolSatelliteRunCancelSubject} (R-022). It publishes one wherever it ends a taken
* attempt without the attempt's terminal event: a cancel, a `deadline-exceeded` or `not-running`
* renew, a request whose `taken` reply never arrived before the dispatcher retries the run, and a
* reply or event that fails its contract. The home operator's adoption pass publishes one too, for
* a regional `running` row it leaves to the reaper (R-116). The satellite deletes that attempt's
* pod. It also records the cancelled `attemptId` and refuses a later request carrying it, because a
* cancel can reach the satellite before the request it cancels.
*
* Every request carries `expiresAt`, the dispatcher's send time plus
* {@link APP_POOL_SATELLITE_REQUEST_TIMEOUT_MS} (R-024). A satellite that dequeues a request after
* `expiresAt` answers `refused` with reason `expired` and creates nothing. So a stale request that
* waited in a slow satellite past the dispatcher's timeout never runs a pod beside the retry's. The
* satellite also answers `expired` to a request whose `expiresAt` lies more than twice the timeout
* past its own clock, the skew allowance, so a far-future instant cannot switch the check off
* (R-029). A cancel carries an `expiresAt` too. The dispatcher copies it from the request it cancels.
* The adoption pass has no copy, because the request's value is not persisted, so it computes a
* fresh one: its own clock plus {@link APP_POOL_SATELLITE_REQUEST_TIMEOUT_MS}, which is later than
* the original (R-116). The satellite keeps the cancel's record until the earlier of that
* `expiresAt` and one request timeout past the cancel's arrival on its own clock (R-116). A record
* kept that long covers every queued copy of the request, and a cancel carrying a far-future
* instant pins nothing for the process's life.
*
* A cancel that NATS drops leaves two bounds, one per phase of the attempt. Before the pod accepts
* the job, the satellite executor (WS-10) bounds the wait at the snapshot's `deadlineMs` plus
* 1,020 s, counted from `taken` (R-116). The 1,020 s is the home runner's three pre-accept clocks in
* sum: `APP_POOL_SCHEDULING_TIMEOUT_MS`, `APP_POOL_IMAGE_PULL_TIMEOUT_MS` and
* `ISOLATED_POOL_READY_TIMEOUT_MS` in `@zion/sdk-contracts` `isolated-pool-pipe.ts`. The `deadlineMs`
* term covers a wait for capacity. At home a capacity wait has no clock of its own, and the run
* deadline ends it; the satellite's bound gives a regional pool the same wait. Past the bound the
* satellite publishes `error` with reason `timeout` and deletes the pod, the reason the home arm
* records when its deadline ends a capacity wait. After the pod accepts, the snapshot's
* `deadlineMs` bounds the handler, counted from the accept.
*
* Every subject sits under `zion.satellite.<dataPlaneId>.<region>.` (R-016). The NATS grants R-026
* names have one declaration, {@link satelliteNatsGrants} and {@link homeSatelliteNatsGrants} (R-052),
* which the DP chart (WS-11) and core-api's mint arm (WS-14) read. The satellite user for region R
* of data plane D subscribes on `zion.satellite.<D>.<R>.run.v1`,
* `zion.satellite.<D>.<R>.run.*.cancel.v1` and `zion.satellite.<D>.<R>.desired.v1`, publishes on
* `zion.satellite.<D>.<R>.>`, answers requests through `allow_responses`, and holds no `_INBOX.>`
* subscription (DF-8). The home operator's user publishes on `zion.satellite.<D>.*.run.>`, which
* covers the run request and every per-run cancel, and on `zion.satellite.<D>.*.desired.v1`, the
* slice of each region. It subscribes on `zion.satellite.<D>.*.>`, which covers the events and the
* status. R-026 wrote the data plane token of the home patterns as `*`; the function writes it out.
* The data plane id and the region are two tokens, so a grant written with one `*` in their place,
* such as `zion.satellite.*.run.>`, matches no satellite subject. The home publish pattern also
* matches every per-run events subject, because `>` takes the run id, `events` and `v1` as well as
* `v1` alone; the home operator publishes on none of them.
*
* The second token is the literal `satellite`, and that token separates the satellite wire from
* every app's NATS grant. An app principal holds publish and subscribe on its own
* `zion.<appScope>.>` subtree, where `appScope` is the app's slug. `satellite` is a member of
* `RESERVED_APP_SCOPES` in `@zion/sdk-contracts` `event-subjects.ts` (R-016), so both
* `createAppSchema` copies refuse the slug with `RESERVED_APP_SLUG_MESSAGE`, and core-api's
* `isNatsSubjectWithinAppScope` refuses the scope when it mints an app credential. No app scope
* can therefore equal the root token, and no app grant covers a satellite subject. The subject test
* `SU-4` reads the second token off every built subject and reds if the token leaves the reserved
* set or the create door stops refusing it.
*
* The data plane id in the third token is a lowercase UUID and the region a region name. Both are
* grammars for routing: two casings of one id would be two subjects that no grant names together.
*
* This module imports no `nats` package, for the reason `@zion/sdk-contracts` `app-pool-subjects.ts`
* states: the operator and the chart tests import it, and a runtime dependency here would reach
* every one of them.
*/
/**
* The subject root every satellite subject starts with (R-016). Its second token, `satellite`, is a
* member of `RESERVED_APP_SCOPES`; see the module doc for why that membership is the control.
*/
const SATELLITE_SUBJECT_ROOT = "zion.satellite";
/**
* True when `dataPlaneId` may stand in a satellite subject's third token: a UUID by
* `APP_ID_UUID_PATTERN`, in lower case.
*
* Lower case only, because a subject is a routing key and two casings of one id would be two
* subjects the NATS grants do not both name. The pattern admits no `.`, `*`, `>` or whitespace.
* Pure.
*/
function isSatelliteSubjectDataPlaneId(dataPlaneId) {
	return APP_ID_UUID_PATTERN.test(dataPlaneId) && dataPlaneId === dataPlaneId.toLowerCase();
}
/** Quote a value in an error message without letting a hostile string forge structure. */
function quoted(value) {
	return JSON.stringify(value);
}
/**
* The shared prefix `zion.satellite.<dataPlaneId>.<region>`, after both tokens are checked.
*
* Throws `RangeError` naming the calling builder when `dataPlaneId` is not a lowercase UUID or
* `region` is outside `APP_POOL_REGION_PATTERN`. Neither grammar admits `.`, `*`, `>` or
* whitespace, so a token can never add a subject level or a wildcard.
*/
function satellitePrefix(builder, dataPlaneId, region) {
	if (!isSatelliteSubjectDataPlaneId(dataPlaneId)) throw new RangeError(`${builder}: dataPlaneId must be a lowercase UUID, got ${quoted(dataPlaneId)}`);
	if (!APP_POOL_REGION_PATTERN.test(region)) throw new RangeError(`${builder}: region must match APP_POOL_REGION_PATTERN, got ${quoted(region)}`);
	return `${SATELLITE_SUBJECT_ROOT}.${dataPlaneId}.${region}`;
}
/**
* The subject a home dispatcher sends a regional attempt's execution request on:
* `zion.satellite.<dataPlaneId>.<region>.run.v1` (R-001, R-016).
*
* Called by the home dispatcher (WS-10) to send the request, by the satellite executor to subscribe,
* and restated by WS-11's NATS grants. Pure.
*
* Inputs: `dataPlaneId`, the data plane's lowercase UUID (`DATA_PLANE_ID`, lower-cased by the
* caller); `region`, the satellite region in `APP_POOL_REGION_PATTERN`'s grammar. Output: six
* tokens, the last `v1`, the envelope version of the request and reply shapes below.
*
* Errors: `RangeError` when either token is outside its grammar. The builder never repairs a value:
* a lower-cased or trimmed id would be a subject no grant names.
*/
function appPoolSatelliteRunSubject(dataPlaneId, region) {
	return `${satellitePrefix("appPoolSatelliteRunSubject", dataPlaneId, region)}.run.v1`;
}
/**
* The subject a satellite streams one run's attempt events on:
* `zion.satellite.<dataPlaneId>.<region>.run.<runId>.events.v1` (R-001, R-010, R-016).
*
* Called by the satellite executor to publish and by the home dispatcher to subscribe. The home
* dispatcher subscribes before it sends the request, so no event published after the reply is
* lost. A retried run reuses the subject; the `attemptId` every event carries is the fence that
* tells one attempt's events from another's. Pure.
*
* The subject has eight tokens and {@link appPoolSatelliteRunSubject} six, so the satellite's
* exact-subject subscription on the run subject never receives an event, whatever the run id.
*
* Errors: `RangeError` for a bad `dataPlaneId` or `region`, as above, and when `runId` is outside
* `WORK_RUN_ID_PATTERN` (26 lowercase Crockford base32 characters, which admit no `.`, `*`, `>`).
*/
function appPoolSatelliteRunEventsSubject(dataPlaneId, region, runId) {
	const prefix = satellitePrefix("appPoolSatelliteRunEventsSubject", dataPlaneId, region);
	if (!workRunIdSchema.safeParse(runId).success) throw new RangeError(`appPoolSatelliteRunEventsSubject: runId must match WORK_RUN_ID_PATTERN, got ${quoted(runId)}`);
	return `${prefix}.run.${runId}.events.v1`;
}
/**
* The subject a satellite publishes its status on: `zion.satellite.<dataPlaneId>.<region>.status.v1`
* (D-19, R-005, R-016).
*
* Called by the satellite operator once per reconcile cycle to publish a
* `SatelliteStatusMessageSchema` message, and by the home operator to subscribe. The home operator
* accepts a message only when the subject's region is an entry of `desired.satellites` and equals
* the message's own `region`. Pure. Errors: `RangeError` as {@link appPoolSatelliteRunSubject}.
*/
function appPoolSatelliteStatusSubject(dataPlaneId, region) {
	return `${satellitePrefix("appPoolSatelliteStatusSubject", dataPlaneId, region)}.status.v1`;
}
/**
* The subject a home dispatcher publishes a stop for one run's handed-off attempt on:
* `zion.satellite.<dataPlaneId>.<region>.run.<runId>.cancel.v1` (R-022).
*
* Called by the home dispatcher (WS-10) to publish an {@link appPoolSatelliteRunCancelSchema}
* message, and by the satellite executor, which subscribes on the region's wildcard
* `zion.satellite.<dp>.<region>.run.*.cancel.v1`. Pure.
*
* The subject has eight tokens, like {@link appPoolSatelliteRunEventsSubject}, and differs from it
* in the seventh: `cancel` where the events subject has `events`. So the home dispatcher's per-run
* events subscription never receives a cancel, the satellite's cancel subscription never receives
* an event, and the six-token run subscription receives neither.
*
* Errors: `RangeError` for a bad `dataPlaneId`, `region` or `runId`, as
* {@link appPoolSatelliteRunEventsSubject}.
*/
function appPoolSatelliteRunCancelSubject(dataPlaneId, region, runId) {
	const prefix = satellitePrefix("appPoolSatelliteRunCancelSubject", dataPlaneId, region);
	if (!workRunIdSchema.safeParse(runId).success) throw new RangeError(`appPoolSatelliteRunCancelSubject: runId must match WORK_RUN_ID_PATTERN, got ${quoted(runId)}`);
	return `${prefix}.run.${runId}.cancel.v1`;
}
/**
* The subject a home operator publishes one region's desired-state slice on:
* `zion.satellite.<dataPlaneId>.<region>.desired.v1` (2026-10-02-regional-pools-release-2 D-7).
*
* Called by the home operator's slice publisher (WS-9) once per enabled region per reconcile, and
* by the satellite's slice consumer (WS-10), which subscribes on its own region's subject only.
* The message is an {@link appPoolSatelliteDesiredSliceSchema} slice. Pure.
*
* The subject has six tokens, like {@link appPoolSatelliteRunSubject} and
* {@link appPoolSatelliteStatusSubject}, and differs from both in the fifth: `desired`. So the
* satellite's exact run subscription never receives a slice, and the slice subscription never
* receives a run request.
*
* Errors: `RangeError` as {@link appPoolSatelliteRunSubject}.
*/
function appPoolSatelliteDesiredSubject(dataPlaneId, region) {
	return `${satellitePrefix("appPoolSatelliteDesiredSubject", dataPlaneId, region)}.desired.v1`;
}
/**
* The NATS permissions of the satellite operator for `region` of data plane `dataPlaneId`, the
* `satellite-<region>` user (R-026, R-052, DF-8).
*
* Called by core-api's mint arm (WS-14), which the home operator asks for the satellite's credential,
* and by the DP chart's grant spec (WS-11). Pure.
*
* Output, for data plane D and region R:
* - `subscribe`: `zion.satellite.<D>.<R>.run.v1`, the run request,
*   `zion.satellite.<D>.<R>.run.*.cancel.v1`, every per-run cancel, and
*   `zion.satellite.<D>.<R>.desired.v1`, its own region's desired-state slice
*   (2026-10-02-regional-pools-release-2 D-7);
* - `publish`: `zion.satellite.<D>.<R>.>`, its events and its status;
* - `allowResponses`: `true`, so it answers the run request on the requester's reply subject.
*
* No pattern names `_INBOX`, another region or another data plane, so the satellite reads no reply
* addressed to another client and nothing of any other satellite, another region's slice included.
* The publish pattern also covers the region's own run, cancel and slice subjects. A satellite that
* published there would reach only itself, because the home operator holds no subscription on any
* of the three.
*
* Errors: `RangeError` naming `satelliteNatsGrants` when `dataPlaneId` is not a lowercase UUID or
* `region` is outside `APP_POOL_REGION_PATTERN`, the rule the subject builders apply. Neither grammar
* admits `.`, `*`, `>` or whitespace, so an argument never widens a pattern.
*/
function satelliteNatsGrants(dataPlaneId, region) {
	const prefix = satellitePrefix("satelliteNatsGrants", dataPlaneId, region);
	return {
		subscribe: [
			appPoolSatelliteRunSubject(dataPlaneId, region),
			`${prefix}.run.*.cancel.v1`,
			appPoolSatelliteDesiredSubject(dataPlaneId, region)
		],
		publish: [`${prefix}.>`],
		allowResponses: true
	};
}
/**
* The NATS permissions the home operator of data plane `dataPlaneId` needs on the satellite wire of
* every region it enables (R-026, R-052).
*
* Called by the DP chart's grant spec (WS-11). The live home operator's credential holds `>` today,
* and these patterns are inside it; the function states the part of it the satellite wire needs.
* Pure.
*
* Output, for data plane D:
* - `publish`: `zion.satellite.<D>.*.run.>`, the run request and every per-run cancel of each region,
*   and `zion.satellite.<D>.*.desired.v1`, the desired-state slice of each region
*   (2026-10-02-regional-pools-release-2 D-7);
* - `subscribe`: `zion.satellite.<D>.*.>`, the events and the status of each region;
* - `allowResponses`: `false`, since the home operator answers no satellite request.
*
* The region is one `*` token and the data plane id is written out. R-026 wrote the data plane
* token as `*` too, and a home NATS serves one data plane, so the written id refuses nothing a real
* home operator sends and keeps another data plane's subtree out of the grant. The home publish
* pattern also matches each per-run events subject, because `>` takes `<runId>.events.v1`; the home
* operator publishes on none.
*
* The reply to a run request is outside these patterns. nats.js `request()` reads it on the client's
* `_INBOX` subject, which the home operator's own credential grants. R-026 names no `_INBOX` pattern
* for the home user, and this function adds none.
*
* Errors: `RangeError` naming `homeSatelliteNatsGrants` when `dataPlaneId` is not a lowercase UUID.
*/
function homeSatelliteNatsGrants(dataPlaneId) {
	if (!isSatelliteSubjectDataPlaneId(dataPlaneId)) throw new RangeError(`homeSatelliteNatsGrants: dataPlaneId must be a lowercase UUID, got ${quoted(dataPlaneId)}`);
	const dataPlaneRoot = `${SATELLITE_SUBJECT_ROOT}.${dataPlaneId}`;
	return {
		subscribe: [`${dataPlaneRoot}.*.>`],
		publish: [`${dataPlaneRoot}.*.run.>`, `${dataPlaneRoot}.*.desired.v1`],
		allowResponses: false
	};
}
/** `appKey` cap, the same app-key budget `appPoolNodeGroupSpecSchema` carries. */
const SATELLITE_RUN_APP_KEY_MAX_LENGTH = 63;
/**
* The grammar of a standing member set's object name: `apm-` plus 20 lowercase hex characters, the
* output of the operator's `appPoolMemberObjectName` (`APP_POOL_MEMBER_OBJECT_PREFIX` and
* `MEMBER_OBJECT_NAME_HEX_LENGTH` in `app-pool-standing-members.ts`). The operator's own spec pins
* the two together by parsing a request that carries a computed name. Anchored, no quantifier
* nesting, linear on any input.
*/
const APP_POOL_MEMBER_NAME_PATTERN = /^apm-[0-9a-f]{20}$/;
/** The highest TCP port. */
const MAX_TCP_PORT = 65535;
/** Converts the pool's `maxDurationSeconds` to the snapshot's `deadlineMs` unit. */
const MILLISECONDS_PER_SECOND = 1e3;
/** The message of the refine that refuses a pool with both placement forms or with neither (D-16). */
const POOL_PLACEMENT_FORMS_MESSAGE = "a pool declares class or tiers, exactly one (D-16)";
/** The message of the refine that refuses one GPU field without the other on a pool with GPU tiers
*  and CPU tiers. */
const POOL_GPU_PAIR_MESSAGE = "gpu and gpuResourceLimit are both present or both absent (R-115)";
/** The message of the refine that refuses a `gpu` that names no GPU tier of a pool with a GPU tier. */
const POOL_GPU_TIER_MESSAGE = "gpu must name the accelerator type and the gpuCount of one GPU tier of the pool";
/** The message of the refine that refuses a CPU `nodeType` outside every CPU tier family of a pool
*  with tiers (2026-10-24-pool-machine-generations R-008). */
const POOL_CPU_TIER_MESSAGE = "nodeType must be a machine type of the family of one CPU tier of the pool (R-008)";
/** The message of the refine that refuses a GPU `nodeType` of no family that carries the
*  accelerator of a GPU tier that `gpu` names (2026-10-24-pool-machine-generations D-10, D-26). */
const POOL_GPU_NODE_TYPE_MESSAGE = "nodeType must be a machine type of a family that carries the accelerator of one GPU tier of the pool that gpu names";
/**
* How long the home dispatcher waits for the satellite's reply to one execution request, in
* milliseconds: 10,000 (R-024).
*
* Both ends read this one figure. The home dispatcher (WS-10) sets each request's `expiresAt` to its
* send time plus this value and gives up on the request when no reply arrives within it; it then
* cancels that attempt and retries the run with a new attempt. The satellite executor refuses, with
* reason `expired`, a request it dequeues after `expiresAt` and a request whose `expiresAt` lies
* more than twice this value past its own clock, the skew allowance (R-029).
*
* The value is the request timeout the operator already gives its other synchronous peers:
* `REQUEST_TIMEOUT_MS` in `cp-client.ts` and `DEFAULT_REQUEST_TIMEOUT_MS` in
* `core-api-instance-client.ts`, both 10,000 (DF-2). The satellite replies once the snapshot parses
* and the attempt is registered, before any pod create (R-013), so the reply needs no scheduling or
* image-pull time.
*/
const APP_POOL_SATELLITE_REQUEST_TIMEOUT_MS = 1e4;
/** The one bound on `expiresAt`, shared by the request and the cancel so the two cannot drift: a
*  whole number of epoch milliseconds from 1 to `Number.MAX_SAFE_INTEGER` (R-024, R-029). zod 4's
*  `.int()` refuses an unsafe integer. */
const satelliteExpiresAtSchema = z.number().int().positive();
/**
* The container `limits` entry a GPU attempt pod carries: `{ 'nvidia.com/gpu': '<count>' }`, the
* `ComputePlacement.gpuResourceLimit` shape the operator's resolver builds (DF-2, DF-4).
*
* The count is a decimal string with no sign and no leading zero, 1 to `MAX_GPU_COUNT` (8), the
* `String(entry.gpu.count)` of the resolved catalog entry. `.strict()`: the one key and no other.
* A conforming object parses to itself; `'0'`, `'01'`, `'9'`, `'1.0'`, a number, or a second key is
* a `ZodError`. Pure.
*/
const satelliteGpuResourceLimitSchema = z.object({ "nvidia.com/gpu": z.string().regex(/^[1-9][0-9]?$/).refine((n) => Number(n) <= MAX_GPU_COUNT, { message: `at most ${MAX_GPU_COUNT} GPUs` }) }).strict();
/**
* The execution snapshot a home dispatcher hands a satellite for one attempt of a regional pool
* (R-001, R-009). Everything the satellite executor needs to build, dial and tear down the attempt pod,
* and nothing it would have to look up: the satellite opens no database and polls no desired state.
*
* Produced by the home dispatcher (WS-10) from the claimed row, the resolved pool view and the
* grant; parsed by the satellite executor before it creates anything. Members:
*
* - `region`: the satellite's region. Equal to `pool.region` (refine) and to the subject's region
*   token, which the satellite checks against its own `SATELLITE_REGION`.
* - `appKey`: the app, in the label-value grammar, at most 63 characters. Pod labels read it.
* - `orgId`: the CP org id of the run, a lowercase UUID, never null: a regional pool is out-of-mesh
*   (`pool-region-requires-out-of-mesh`) and admission refuses an out-of-mesh run with no org.
* - `groupId`: the regional node group the pod's `nodeSelector` and toleration name, in the `apg-`
*   grammar; the home compiler derived it with `appPoolNodeGroupId` including the region. The
*   request binds it to its own org (refine, 2026-10-02-regional-pools-release-2 R-014, chain D):
*   `groupId` must equal `appPoolNodeGroupId({ appKey, orgId, workload: 'out-of-mesh', nodeType,
*   region, diskSizeGb, tier })` over this request's members. So a request cannot place org A's attempt
*   on org B's group, on another app's group, or on a group of another region, machine type or
*   disk size. The satellite places the pod on `groupId` as sent, so this refine is the check.
*   `tier` is absent for a pool with `class`, whose group carries no tier key (DEF-8). For a pool
*   with `tiers` it is absent, or the key of a declared tier of the request's machine
*   (2026-10-24-pool-machine-generations D-26): the CPU family of `nodeType` when the request
*   carries no `gpu`, the accelerator and count of `gpu` on a family of `nodeType` that carries
*   it when the request carries one. So the id is the tierless id of the machine, or the id of
*   one such tier: the key of a tier the pool does not declare is refused. The tierless id stays
*   valid on a pool with `tiers` because the home compiler hashes no tier key into a group id
*   until the S2b compiler row (WS-2b) passes one; the `nodeType` refines bind the machine to a
*   declared tier either way.
* - `nodeType`: the resolved machine type of the pool's regional group, the `nodeType` the home
*   compiler hashed into `groupId`, in the label-value grammar, at most
*   `APP_POOL_NODE_TYPE_MAX_LENGTH` (40) characters. The home dispatcher (WS-9) copies it from the
*   resolved view (R-014). The satellite reads it for the binding refine only.
*   For a pool with `tiers` and a request with neither GPU field, it is a machine type of the
*   family of one CPU tier of the pool, on that tier's cloud (refine,
*   2026-10-24-pool-machine-generations R-008). For a pool with `tiers` and a request with `gpu`
*   that names a GPU tier, it is a machine type of a family that the accelerator table lists for
*   that tier's model on that tier's cloud (refine, D-10, D-26): `g2-standard-4` for an `l4` tier,
*   never `c4d-standard-8`.
* - `diskSizeGb`: the boot disk size of that group in GiB, a whole number from 10 to 4096, present
*   exactly when the pool declares `diskSizeGb`, and then equal to `pool.diskSizeGb` (refine): the
*   two members state one fact. The home dispatcher copies it from the resolved view (R-014).
* - `pool`: the pool's manifest entry, the out-of-mesh arm, verbatim from the envelope. It carries
*   the node vector, `gracePeriodSeconds`, `maxDurationSeconds` and `image.entrypoint` the pod
*   builder reads. `lifetime` is `per-attempt` or `standing` (2026-10-02-regional-pools-release-2
*   §2.1). A `per-attempt` request asks the satellite to create a pod; a `standing` request asks it
*   to lend a member of the set `memberName` names.
* - `memberName`: the object name of the standing member set the attempt borrows from, present
*   exactly when `pool.lifetime` is `standing` (refine). It is the name
*   `appPoolMemberObjectName(appKey, poolName, orgId)` gives in the operator, `apm-` plus 20
*   lowercase hex characters, the name of the member Deployment the satellite runs from its slice.
*   The home dispatcher computes it from the row; the satellite recomputes it from `appKey`, the
*   frame's pool name and `orgId`, and refuses a request whose name differs with `attempt-failed`,
*   so a request cannot borrow another org's member (R-006). A satellite with no free member in the set
*   answers `taken` and then publishes `error` with reason `executor-unavailable`, and the home
*   executor's standing arm reports that error, the outcome the home executor gives when `acquire`
*   answers `null` (2026-10-02-regional-pools-release-2 R-036).
* - `frame`: the run frame the pod receives as its request `args`, as the home executor would write
*   it: pool name, `runId`, `attemptId`, the data plane org id, the compute vector and the payload.
*   Its `orgId` is never null (refine). The satellite writes the frame and never edits it.
* - `imageRef`: the digest-pinned pull reference the home row carries, registry prefix included,
*   equal to the pod's `spec.containers[0].image`.
* - `imageVersion`: the plain version string of that image, the pod's `zion.witness.ai/version`
*   label source (chain A hop 7). R-009 adds it to R-001's list, because the pod builder requires it.
* - `grant`: the CP-authored posture for the out-of-mesh pool. It must not exceed the pool's own
*   declaration: the refine runs `assertAppPoolGrantConsistent`, so a widened grant is refused at the
*   satellite as well as at the CP.
* - `podPort`: the pipe port the pod listens on and the satellite dials, 1 to 65535.
* - `deadlineMs`: the wall-clock milliseconds the handler may take. The home dispatcher (WS-10)
*   computes it when it builds the snapshot, as
*   `Math.min(row.timeoutMs, pool.maxDurationSeconds * 1000)`, where `pool` is the entry it
*   resolved at dispatch time and carries in this same snapshot (R-018). The clamp is the
*   dispatcher's. Admission capped `timeoutMs` against the pool view at submit time, and a publish
*   that lowers `maxDurationSeconds` while the run is queued makes that cap stale; the clamp gives
*   the regional run what the home pod server's own clamp gives a home run. The refine checks
*   `deadlineMs` against the carried `pool` only, so a clamped snapshot passes it by construction,
*   and an unclamped one is refused. The satellite writes it verbatim as
*   `isolatedJobRequestSchema`'s `deadlineMs`, measured from the moment the pod accepts the
*   request. When the handler runs past it, the satellite publishes an `error` event with reason
*   `timeout` and deletes the pod. A positive integer, at most `pool.maxDurationSeconds * 1000`
*   (7,200,000 at the largest pool). Without it a run submitted with `timeoutMs: 60000` on a 600 s
*   pool would hold its GPU node for up to 600 s after home had finished the row `timeout`.
* - `gpuResourceLimit`: the `{ 'nvidia.com/gpu': '<count>' }` entry the pod's container `limits`
*   carry, and `requests` never do (DF-4). The home dispatcher copies it from the placement the home
*   resolver built against the satellite's catalog entry, because the satellite holds no catalog
*   (R-001) and the pool entry cannot carry a GPU count (`COMPUTE_VECTOR_KEYS` has no GPU key).
*   Required when `pool.class` is a GPU class (`isGpuComputeClass`), and refused for any other class
*   (refine, R-009): a GPU class resolves only to GPU machine families and every other class only to
*   families with no GPU. Without it a regional GPU pool's pod would run on a GPU node with no
*   device assigned. A pool with `tiers` (2026-10-24-pool-machine-generations D-16, R-003) may
*   carry it when at least one tier is a GPU tier, and may omit it when at least one tier is a CPU
*   tier: a pool can hold GPU tiers on one cloud and CPU tiers on the other, and the snapshot does
*   not name the cloud of the tier the home resolver chose. On such a pool it and `gpu` are both
*   present or both absent (refine), because the two members state one fact.
* - `gpu`: the accelerator of the regional node group the pod lands on, `{ acceleratorType, count }`
*   in `appPoolNodeGroupGpuSchema`'s grammar (R-115). The home dispatcher copies it from the view the
*   home resolver built, the same value the home compiler wrote on the group's `gpu`. The satellite
*   executor builds its pool view from it, so the pod's limit comes through `gpuLimitField(view)`
*   from real data and the view names the accelerator the node carries. Present when `pool.class`
*   is a GPU class and absent for any other class; for a pool with `tiers`, present or absent by the
*   rule `gpuResourceLimit` follows (refine). Its `count` equals the limit's count (refine): the two
*   members state one fact, and a snapshot where they disagree is refused before any pod exists.
*   For a pool with a GPU tier, the pair names one GPU tier of the pool (refine): its `count` is
*   that tier's `gpuCount`, and its `acceleratorType` is one of `acceleratorTypesFor(tier.accelerator,
*   tier.cloud, tier.gpuCount)`. So the node a snapshot names is a node a tier declares, and a
*   snapshot that names an H100 type for a pool of L4 tiers is refused.
* - `expiresAt`: the instant after which the satellite must not start this attempt, in epoch
*   milliseconds (R-024). The home dispatcher sets it to its own send time plus
*   {@link APP_POOL_SATELLITE_REQUEST_TIMEOUT_MS}. A satellite that dequeues the request when its
*   own clock reads later than `expiresAt` answers `refused` with reason `expired`, registers
*   nothing and creates nothing. A satellite that takes the request before `expiresAt` runs it to
*   the end; `expiresAt` bounds the wait for the reply and never the attempt. The two clusters' node
*   clocks both follow GKE's time sync, so the check moves by their skew, which is far below the
*   timeout. The satellite also answers `refused` with reason `expired`, and creates nothing, when
*   `expiresAt` lies more than twice {@link APP_POOL_SATELLITE_REQUEST_TIMEOUT_MS} (20,000 ms) past
*   its own clock (R-029). A correct dispatcher sets it at most one timeout ahead, so the second
*   timeout is the skew allowance. Without that ceiling a dispatcher that wrote microseconds, or
*   any far-future instant, would never be refused `expired`. A whole number from 1 to `Number.MAX_SAFE_INTEGER`, the same bound
*   the cancel's `expiresAt` takes; the schema cannot read a clock, so it refuses neither a past
*   nor a far-future instant, and the satellite's two clock checks do.
*
* None of the members is a credential. The satellite pod holds none (R-007), and the snapshot
* carries none to hand it.
*
* `.strict()` at every level. A conforming object parses to itself; a missing or unknown key, a
* mismatched region, a standing pool with no `memberName`, a per-attempt pool with one, a null
* frame org, a `groupId` that is not the request org's own regional group, a `diskSizeGb` other
* than `pool.diskSizeGb`, a widened grant, a pool that declares both `class` and `tiers` or
* neither (at `pool.tiers`, 2026-10-24-pool-machine-generations D-16), a GPU pool with no
* `gpuResourceLimit` or a non-GPU pool with one, a GPU pool with no `gpu` or a non-GPU pool with
* one, a `gpu` without a `gpuResourceLimit` or the reverse on a pool with GPU tiers and CPU tiers,
* a `gpu.count` other than the limit's count, a `gpu` that names no GPU tier of a pool with
* a GPU tier (its accelerator type and its count together), a request with neither GPU field whose
* `nodeType` is of no CPU tier family of a pool with a CPU tier (at `nodeType`,
* 2026-10-24-pool-machine-generations R-008), a request whose `gpu` names a GPU tier and whose
* `nodeType` is of no family that carries that tier's model (at `nodeType`, D-10, D-26), a GPU pool (a GPU class, or a pool with a GPU
* tier) that declares `containerRuntime: true` (R-032, R-113), a `deadlineMs` above the pool's
* `maxDurationSeconds`, or an `expiresAt` that is not a positive whole number is a `ZodError`. The
* satellite answers a failed parse with a `refused` reply and creates nothing. Pure.
*
* The GPU and container-runtime refusal is the publish rule `pool-gpu-requires-default-sandbox` in
* `@zion/sdk-contracts` read at the wire (R-032, option 2; DF-3), and for a pool with `tiers` the
* rule `pool-gpu-tier-requires-default-sandbox`, which refuses any GPU tier on a pool that declares
* the runtime (2026-10-24-pool-machine-generations R-003). The home resolver refuses the pool
* at placement under that id and never hands it off, so the refine fires only on a dispatcher that
* skipped that refusal, and then the satellite builds no pod that pairs a GPU device with the
* unconfined arm. It reads `pool.containerRuntime` because the grant cannot exceed the pool
* (`assertAppPoolGrantConsistent`), so a pool that declares no container runtime has a grant with
* none.
*
* Size: the frame's `payload` is the submitter's body, and NATS refuses a message above the server's
* `max_payload` (1 MiB by default). The home dispatcher checks the encoded request against that
* limit before it sends, and ends the attempt `payload-too-large` when it is over.
*/
const appPoolSatelliteRunRequestSchema = z.object({
	region: appPoolRegionSchema,
	appKey: z.string().regex(APP_POOL_LABEL_VALUE_PATTERN).max(SATELLITE_RUN_APP_KEY_MAX_LENGTH),
	orgId: appPoolOrgIdSchema,
	groupId: z.string().regex(APP_POOL_NODE_GROUP_ID_PATTERN),
	nodeType: z.string().regex(APP_POOL_LABEL_VALUE_PATTERN).max(APP_POOL_NODE_TYPE_MAX_LENGTH),
	diskSizeGb: appPoolNodeGroupSpecSchema.shape.diskSizeGb,
	pool: appPoolOutOfMeshSchema,
	frame: appPoolRunFrameSchema,
	imageRef: appPoolImagePullRefSchema,
	imageVersion: appVersionStringSchema,
	grant: AppPoolGrantSchema,
	podPort: z.number().int().min(1).max(MAX_TCP_PORT),
	deadlineMs: z.number().int().positive(),
	gpuResourceLimit: satelliteGpuResourceLimitSchema.optional(),
	gpu: appPoolNodeGroupGpuSchema.optional(),
	expiresAt: satelliteExpiresAtSchema,
	memberName: z.string().regex(APP_POOL_MEMBER_NAME_PATTERN).optional()
}).strict().refine((r) => r.pool.region === r.region, {
	message: "pool.region must equal the request region",
	path: ["pool", "region"]
}).refine((r) => r.pool.lifetime === "standing" === (r.memberName !== void 0), {
	message: "memberName is required for a standing pool and refused for a per-attempt pool",
	path: ["memberName"]
}).refine((r) => r.frame.orgId !== null, {
	message: "an out-of-mesh frame carries an org",
	path: ["frame", "orgId"]
}).refine((r) => grantFitsPool(r.pool, r.grant), {
	message: "grant exceeds the pool declaration",
	path: ["grant"]
}).refine((r) => poolHasOnePlacementForm(r.pool), {
	message: POOL_PLACEMENT_FORMS_MESSAGE,
	path: ["pool", "tiers"]
}).refine((r) => r.gpuResourceLimit !== void 0 ? poolMayUseGpu(r.pool) : poolMayUseCpu(r.pool), {
	message: "gpuResourceLimit is required for a GPU class or a pool of GPU tiers, and refused for a pool with no GPU",
	path: ["gpuResourceLimit"]
}).refine((r) => r.gpu !== void 0 ? poolMayUseGpu(r.pool) : poolMayUseCpu(r.pool), {
	message: "gpu is required for a GPU class or a pool of GPU tiers, and refused for a pool with no GPU (R-115)",
	path: ["gpu"]
}).refine((r) => r.gpu === void 0 === (r.gpuResourceLimit === void 0) || !(poolMayUseGpu(r.pool) && poolMayUseCpu(r.pool)), {
	message: POOL_GPU_PAIR_MESSAGE,
	path: ["gpu"]
}).refine((r) => r.gpu === void 0 || poolDeclaresGpu(r.pool, r.gpu), {
	message: POOL_GPU_TIER_MESSAGE,
	path: ["gpu"]
}).refine((r) => r.gpu !== void 0 || r.gpuResourceLimit !== void 0 || poolDeclaresCpuNodeType(r.pool, r.nodeType), {
	message: POOL_CPU_TIER_MESSAGE,
	path: ["nodeType"]
}).refine((r) => r.gpu === void 0 || poolDeclaresGpuNodeType(r.pool, r.nodeType, r.gpu), {
	message: POOL_GPU_NODE_TYPE_MESSAGE,
	path: ["nodeType"]
}).refine((r) => r.gpu === void 0 || r.gpuResourceLimit === void 0 || String(r.gpu.count) === r.gpuResourceLimit["nvidia.com/gpu"], {
	message: "gpu.count must equal the gpuResourceLimit count (R-115)",
	path: ["gpu", "count"]
}).refine((r) => !(poolMayUseGpu(r.pool) && r.pool.containerRuntime === true), {
	message: "a GPU class or a GPU tier runs on the default isolation arm only (pool-gpu-requires-default-sandbox, R-032)",
	path: ["pool", "containerRuntime"]
}).refine((r) => r.deadlineMs <= r.pool.maxDurationSeconds * MILLISECONDS_PER_SECOND, {
	message: "deadlineMs must not exceed pool.maxDurationSeconds",
	path: ["deadlineMs"]
}).refine((r) => r.diskSizeGb === r.pool.diskSizeGb, {
	message: "diskSizeGb must equal pool.diskSizeGb (R-014)",
	path: ["diskSizeGb"]
}).refine((r) => groupIdNamesOrgGroup(r.groupId, {
	appKey: r.appKey,
	orgId: r.orgId,
	nodeType: r.nodeType,
	region: r.region,
	diskSizeGb: r.diskSizeGb
}, r.pool, r.gpu), {
	message: "groupId must be the regional node group of the request org (R-014, chain D)",
	path: ["groupId"]
});
/**
* Whether a pool declares exactly one placement form: `class` or `tiers` (2026-10-24-pool-machine-
* generations D-16). The publish rules refuse a pool with both (`pool-tiers-refuses-field`) and a
* pool with neither (`pool-class-or-tiers-required`). So only a dispatcher or a publisher that
* skipped them sends one. The run request and the slice view refuse it at `pool.tiers` (DEF-1).
*
* Output: `true` when exactly one of `class` and `tiers` is present. `false` when both are present,
* and `false` when neither is. Pure, never throws.
*/
function poolHasOnePlacementForm(pool) {
	return pool.class === void 0 !== (pool.tiers === void 0);
}
/**
* Whether a pool can place an attempt on a node with a GPU (2026-10-24-pool-machine-generations
* R-003, E-1). The wire refines of the run request and of the slice view read it.
*
* Output per input class:
* - a pool whose `class` is a GPU class: `true`, whatever its `tiers` hold;
* - a pool with `tiers`: `true` when at least one tier is a GPU tier. A pool may declare GPU tiers on
*   one cloud and CPU tiers on the other (`pool-tiers-gpu-mismatch` compares counts inside one cloud
*   only), and the wire does not say which cloud's tier the home resolver chose;
* - any other pool: `false`. A missing class is no GPU class.
*
* A pool with both a GPU class and tiers is malformed (D-16), and the refine of
* {@link poolHasOnePlacementForm} refuses it. This predicate still reads the class first, so the GPU
* refines and the sandbox refine never treat such a pool as a pool with no GPU (DEF-1). Pure, never
* throws.
*/
function poolMayUseGpu(pool) {
	if (isGpuComputeClass(pool.class)) return true;
	return pool.tiers?.some((tier) => isAppPoolGpuTier(tier)) ?? false;
}
/**
* Whether a pool can place an attempt on a node with no GPU (2026-10-24-pool-machine-generations
* R-003, E-1), the converse of {@link poolMayUseGpu}.
*
* Output per input class:
* - a pool whose `class` is a GPU class: `false`, whatever its `tiers` hold;
* - a pool with `tiers`: `true` when at least one tier is a CPU tier;
* - a pool with no `tiers`: `true`, a missing class included.
*
* For a pool with `class` and no `tiers`, exactly one of the two predicates is `true`, so the
* refines keep the class rule as it was: a GPU class requires the GPU fields and every other class
* refuses them. Pure, never throws.
*/
function poolMayUseCpu(pool) {
	if (isGpuComputeClass(pool.class)) return false;
	return pool.tiers?.some((tier) => !isAppPoolGpuTier(tier)) ?? true;
}
/**
* Whether an accelerator `{ acceleratorType, count }` is one that a GPU tier of the pool declares
* (2026-10-24-pool-machine-generations R-003, D-10, D-44). The run request and the slice view read
* it on their `gpu` member.
*
* Output per input class:
* - a pool with no GPU tier, `tiers` absent included: `true` for any accelerator. A class pool
*   declares no accelerator on the wire; the accelerator comes from the class catalog. On a pool of
*   CPU tiers the presence refine already refuses a GPU field, and one fault raises one issue;
* - a pool with a GPU tier: `true` when one GPU tier has `gpuCount` equal to `gpu.count`, and
*   `acceleratorTypesFor(tier.accelerator, tier.cloud, tier.gpuCount)` holds `gpu.acceleratorType`.
*   The table maps a model to a set of cloud types (D-44), so both `nvidia-h100-80gb` and
*   `nvidia-h100-mega-80gb` name an `h100` tier of 8 GPUs on `gcp`. A model with no table row for
*   the tier's cloud gives no type, so no accelerator names that tier (DEF-1).
*
* Pure, never throws.
*/
function poolDeclaresGpu(pool, gpu) {
	const gpuTiers = (pool.tiers ?? []).filter(isAppPoolGpuTier);
	if (gpuTiers.length === 0) return true;
	return gpuTiers.some((tier) => gpuNamesTier(tier, gpu));
}
/**
* Whether an accelerator `{ acceleratorType, count }` names one GPU tier: the tier's `gpuCount`
* equals `gpu.count`, and `acceleratorTypesFor(tier.accelerator, tier.cloud, tier.gpuCount)` holds
* `gpu.acceleratorType` (D-10, D-44). The one statement of the rule that {@link poolDeclaresGpu}
* and {@link poolDeclaresGpuNodeType} read. Pure, never throws.
*/
function gpuNamesTier(tier, gpu) {
	return tier.gpuCount === gpu.count && acceleratorTypesFor(tier.accelerator, tier.cloud, tier.gpuCount).includes(gpu.acceleratorType);
}
/**
* Whether a CPU machine type is one that a CPU tier of the pool declares
* (2026-10-24-pool-machine-generations R-008, D-15). The run request reads it when `gpu` and
* `gpuResourceLimit` are both absent, and the slice view reads it on a resolved view whose `gpu`
* is `null`. Both then name a node with no GPU, so the node must be of a CPU tier's family.
*
* Output per input class:
* - a pool with `class`: `true`. The class ladder decides the machine, and a pool with both forms is
*   refused by the placement refine; one fault raises one issue;
* - a pool with no CPU tier, `tiers` absent included: `true`. On a pool of GPU tiers only the
*   presence refines already refuse a request with no GPU field, and a pool with neither form is
*   refused by the placement refine;
* - a pool with a CPU tier: `true` when one CPU tier has `family` equal to the family of `nodeType`
*   on that tier's cloud, `gcpMachineTypeFamily` for `gcp` and `awsMachineTypeFamily` for `aws`
*   (`machineTypeFamilyOnCloud`). So `c4d-standard-8` names a `gcp` `c4d` tier, `c7i-xlarge` an
*   `aws` `c7i` tier, and `n2-standard-8` names neither.
*
* The snapshot does not name its cloud, so a node type whose family reads the same on both clouds
* matches a tier of either. Pure, never throws.
*/
function poolDeclaresCpuNodeType(pool, nodeType) {
	if (pool.class !== void 0) return true;
	const cpuTiers = (pool.tiers ?? []).filter((tier) => !isAppPoolGpuTier(tier));
	if (cpuTiers.length === 0) return true;
	return cpuTiers.some((tier) => tierNamesMachine(tier, nodeType, void 0));
}
/**
* Whether a GPU machine type can carry the accelerator of a GPU tier of the pool that `gpu` names
* (2026-10-24-pool-machine-generations D-10, D-26, DEF-1). The run request reads it when `gpu` is
* present, and the slice view reads it on a resolved view with a non-null `gpu`.
*
* Output per input class:
* - a pool with `class`: `true`. The class catalog decides the machine and its accelerator, and a
*   pool with both forms is refused by the placement refine; one fault raises one issue;
* - a pool with no GPU tier that `gpu` names (no GPU tier with `gpuCount` equal to `gpu.count`
*   whose cloud types hold `gpu.acceleratorType`), `tiers` absent included: `true`. The presence
*   refines or the GPU tier refine at `gpu` already refuse that input;
* - otherwise `true` when one such tier's key fits the machine by `tierFitsGroupMachine`: the
*   family of `nodeType` on the tier's cloud is one of the `families` the accelerator table lists
*   for the tier's model there. So `g2-standard-4` carries an `l4` tier, `a3-megagpu-8g` an `h100`
*   tier of 8 GPUs, and `c4d-standard-8` neither.
*
* Pure, never throws.
*/
function poolDeclaresGpuNodeType(pool, nodeType, gpu) {
	if (pool.class !== void 0) return true;
	const named = (pool.tiers ?? []).filter(isAppPoolGpuTier).filter((tier) => gpuNamesTier(tier, gpu));
	if (named.length === 0) return true;
	return named.some((tier) => tierNamesMachine(tier, nodeType, gpu));
}
/** `assertAppPoolGrantConsistent` as a predicate, so the refine reports a `ZodError` in place of a
*  thrown `Error`. The assertion stays the one statement of the rule. */
function grantFitsPool(pool, grant) {
	try {
		assertAppPoolGrantConsistent(pool, grant);
		return true;
	} catch {
		return false;
	}
}
/**
* The out-of-mesh node group id of one org's regional group, or `null` when the identity cannot
* hash (2026-10-02-regional-pools-release-2 R-014, DF-6, chain D).
*
* Read by {@link groupIdNamesOrgGroup}, so the run request and the slice bind a group to its org
* by one rule. Output per input class:
* - a `region` outside `APP_POOL_REGION_PATTERN`, or a `diskSizeGb` that is present and outside the
*   node group's 10 to 4096 whole-number bounds: `null`. zod runs a refine after a pattern or range
*   issue in a member, and {@link appPoolNodeGroupId} throws `RangeError` on these two values, so
*   this guard turns the throw into a refusal: no `apg-` string equals `null`;
* - otherwise `appPoolNodeGroupId({ appKey, orgId, workload: 'out-of-mesh', nodeType, region,
*   diskSizeGb, tier })`. `tier` is absent, or a key that {@link groupIdNamesOrgGroup} passes only
*   when `isAppPoolNodeGroupTierKey` admits it, so it never makes the hash throw.
*
* Pure. It never throws: `orgId` is a string, so the workload and org pair is always coherent.
*/
function derivedOutOfMeshGroupId(identity) {
	const { appKey, orgId, nodeType, region, diskSizeGb, tier } = identity;
	if (!appPoolRegionSchema.safeParse(region).success || !appPoolNodeGroupSpecSchema.shape.diskSizeGb.safeParse(diskSizeGb).success) return null;
	return appPoolNodeGroupId({
		appKey,
		orgId,
		workload: "out-of-mesh",
		nodeType,
		region,
		diskSizeGb,
		tier
	});
}
/**
* Whether one declared tier of a pool names a machine (2026-10-24-pool-machine-generations R-008,
* D-10, D-26). The machine is `nodeType`, and `gpu` when the node carries an accelerator.
*
* Output per input class:
* - `gpu` absent: `true` when `tier` is a CPU tier whose `family` equals the family of `nodeType`
*   on the tier's cloud (`machineTypeFamilyOnCloud`);
* - `gpu` present: `true` when `tier` is a GPU tier that `gpu` names ({@link gpuNamesTier}) and
*   whose key fits the machine by `tierFitsGroupMachine`: the family of `nodeType` on the tier's
*   cloud is one of the `families` the accelerator table lists for the tier's model there.
*
* It reads the tier's fields, not its key, so a tier whose key the hash refuses still names its
* machine. Read by {@link poolDeclaresCpuNodeType} and {@link groupIdNamesOrgGroup}. Pure, never
* throws.
*/
function tierNamesMachine(tier, nodeType, gpu) {
	if (gpu === void 0) return !isAppPoolGpuTier(tier) && machineTypeFamilyOnCloud(tier.cloud, nodeType) === tier.family;
	return isAppPoolGpuTier(tier) && gpuNamesTier(tier, gpu) && tierFitsGroupMachine(appPoolTierKey(tier), nodeType, gpu);
}
/**
* Whether `groupId` is the regional node group of one org, of the pool's machine, tierless or for
* one tier of the pool that names that machine (2026-10-02-regional-pools-release-2 R-014, chain D;
* 2026-10-24-pool-machine-generations D-26).
*
* Read by the run request's `groupId` refine and by the slice's membership refine. `gpu` is the
* request's `gpu`, or the view's non-null `gpu`. Output per input class:
* - a pool with `tiers` of which no declared tier names the machine ({@link tierNamesMachine}):
*   `true`. A refine of the same schema refuses that input: the CPU tier refine or the GPU node
*   refine at `nodeType`, the GPU tier refine at `gpu`, or a presence or pair refine. So one
*   fault raises one issue, and the input is refused all the same;
* - a `region` or `diskSizeGb` that {@link derivedOutOfMeshGroupId} cannot hash: `false`;
* - otherwise `true` exactly when `groupId` equals the derived id with no tier, or with the key
*   `appPoolTierKey(tier)` of one declared tier that names the machine and whose key
*   `isAppPoolNodeGroupTierKey` admits. So a pool with `class` binds the tierless id alone, and a
*   pool with `tiers` binds the tierless id or the id of a declared tier of the machine: the key of
*   a tier the pool does not declare, or of a declared tier of another machine, is refused.
*
* The tierless id stays valid on a pool with `tiers`: a group of a class pool carries no tier key
* (DEF-8), and the home compiler hashes no tier key into any group id until the S2b compiler row
* (WS-2b) passes one (review rows WS-2a-P2-R3, R4). A tier that names the machine with a key the
* hash refuses adds no id, and the tierless id still binds, so no such tier opens the binding to
* any id. Two tiers can name one machine (`gcp.c4d.spot` and `gcp.c4d.on-demand` both name
* `c4d-standard-8`). The org is in every id, so no tier opens another org's group (chain D).
*
* Pure. It never throws: a key reaches the hash only when `isAppPoolNodeGroupTierKey` admits it.
*/
function groupIdNamesOrgGroup(groupId, identity, pool, gpu) {
	const named = (pool.tiers ?? []).filter((tier) => tierNamesMachine(tier, identity.nodeType, gpu));
	if (pool.tiers !== void 0 && named.length === 0) return true;
	return [void 0, ...named.map((tier) => appPoolTierKey(tier)).filter((key) => isAppPoolNodeGroupTierKey(key))].some((tier) => derivedOutOfMeshGroupId({
		...identity,
		tier
	}) === groupId);
}
/** The reason a refused reply carries, bounded the way `workErrorSchema`'s message is. */
const satelliteRunMessageSchema = z.string().min(1).max(MAX_WORK_ERROR_CHARS);
/**
* The reasons a refused reply may carry: every `WORK_REASONS` member, and `expired` (R-024).
* `expired` is a wire value between two operators and never reaches a run: the home dispatcher has
* already given up on the request it answers. So it stays out of `WORK_REASONS`, which callers read.
*/
const satelliteRunRefusalReasonSchema = z.enum([...WORK_REASONS, "expired"]);
/**
* The satellite's immediate answer to one execution request, sent through `allow_responses` on the
* request's reply subject (R-001, R-013).
*
* - `{ outcome: 'taken', attemptId }`: the snapshot parsed and the satellite registered the attempt
*   under the frame's `attemptId` before this reply and before its first pod create, so a cancel
*   that arrives from here on finds it ({@link appPoolSatelliteRunCancelSchema}). `attemptId` echoes
*   the frame's, and the home dispatcher refuses a reply whose id differs. Events follow on the
*   events subject.
* - `{ outcome: 'refused', reason, message }`: nothing was created. `reason` is a `WORK_REASONS`
*   member or `expired`: `expired` for a request the satellite dequeued after its `expiresAt`
*   (R-024), or whose `expiresAt` lies more than twice {@link APP_POOL_SATELLITE_REQUEST_TIMEOUT_MS}
*   past the satellite's clock (R-029); `attempt-failed` for a snapshot that failed its parse, names another region, or
*   carries a `frame.attemptId` the satellite recorded as cancelled (R-022);
*   `executor-unavailable` for a satellite that cannot create pods now (shutting down, or its
*   apiserver unreachable), which the home dispatcher retries. `message` is 1 to
*   `MAX_WORK_ERROR_CHARS` characters and never quotes the payload. The home dispatcher reads
*   `expired` as `executor-unavailable`; it normally arrives after the dispatcher stopped listening.
*
* No reply within {@link APP_POOL_SATELLITE_REQUEST_TIMEOUT_MS} reads as `executor-unavailable`.
* `.strict()`
* on both arms. A conforming object parses to itself; anything else is a `ZodError`, which the home
* dispatcher treats as `malformed-response`. Pure.
*/
const appPoolSatelliteRunReplySchema = z.discriminatedUnion("outcome", [z.object({
	outcome: z.literal("taken"),
	attemptId: workAttemptIdSchema
}).strict(), z.object({
	outcome: z.literal("refused"),
	reason: satelliteRunRefusalReasonSchema,
	message: satelliteRunMessageSchema
}).strict()]);
/**
* One event of a handed-off attempt, published by the satellite on
* {@link appPoolSatelliteRunEventsSubject} (R-001). Discriminated on `event`, with the five names
* the pod pipe uses. Every arm carries `attemptId`, the fence: the home dispatcher drops an event
* whose `attemptId` is not the attempt it handed off, because a retried run reuses the subject.
*
* - `accepted`: the pod accepted the job. `nodeId` is `appPoolNodeId(spec.nodeName)` of the node the
*   pod runs on and `placedAt` the instant the satellite observed the bind. The home dispatcher
*   calls `onPlaced` and `markPlaced` on it, so a regional run reports its placement at acceptance,
*   later than a home run reports it at the bind (R-014).
* - `progress`: a handler progress frame, `message` clamped to `MAX_WORK_ERROR_CHARS` as the home
*   executor clamps it, `pct` 0 to 100 when the handler sent one.
* - `log`: a handler log chunk, `stream` `stdout` or `stderr`, `chunk` at most
*   `MAX_WORK_LOG_CHUNK_CHARS`, sliced by the satellite as the home executor slices it.
* - `result`: the attempt succeeded. `result` is a `WorkResult`: `inline` with the output, or
*   `pointer` to the artifact the satellite spilled to the data plane's home bucket (D-18), whose
*   size and digest the satellite counted and hashed. `durationMs` is the handler's own time. The
*   schema cannot see the subject, so it cannot compare a pointer's `ref.runId` with the run id in
*   the events subject. The home dispatcher makes that check: a pointer whose `ref.runId` differs
*   from the subject's run id ends the attempt `malformed-response`, and the dispatcher never
*   follows it.
* - `error`: the attempt failed. `reason` is a `WORK_REASONS` member as the home executor would
*   classify it, and `message` 1 to `MAX_WORK_ERROR_CHARS` characters.
*
* `result` and `error` are terminal and exclusive; the satellite publishes nothing for the attempt
* after either. A stream that ends with no terminal is the home dispatcher's to synthesise, as for a
* home attempt. `.strict()` on every arm. A conforming object parses to itself; a message that fails
* the parse is a platform defect, and the home dispatcher ends the attempt `malformed-response`.
* Pure.
*/
const appPoolSatelliteRunEventSchema = z.discriminatedUnion("event", [
	z.object({
		event: z.literal("accepted"),
		attemptId: workAttemptIdSchema,
		nodeId: appPoolNodeIdSchema,
		placedAt: z.string().datetime()
	}).strict(),
	z.object({
		event: z.literal("progress"),
		attemptId: workAttemptIdSchema,
		message: z.string().max(MAX_WORK_ERROR_CHARS),
		pct: z.number().int().min(0).max(100).optional()
	}).strict(),
	z.object({
		event: z.literal("log"),
		attemptId: workAttemptIdSchema,
		stream: z.enum(["stdout", "stderr"]),
		chunk: z.string().max(MAX_WORK_LOG_CHUNK_CHARS)
	}).strict(),
	z.object({
		event: z.literal("result"),
		attemptId: workAttemptIdSchema,
		result: workResultSchema,
		durationMs: z.number().int().nonnegative()
	}).strict(),
	z.object({
		event: z.literal("error"),
		attemptId: workAttemptIdSchema,
		reason: z.enum(WORK_REASONS),
		message: satelliteRunMessageSchema
	}).strict()
]);
/**
* The stop a home dispatcher publishes for one handed-off attempt on
* {@link appPoolSatelliteRunCancelSubject}: `{ attemptId, expiresAt }`, the attempt the dispatcher
* handed off and an instant at or after the `expiresAt` its request carried (R-029, R-116).
*
* Published by the home operator (WS-10) as a plain publish with no reply subject. The home
* dispatcher publishes one wherever it ends a taken attempt without the attempt's terminal event:
* - the caller cancelled the run;
* - a heartbeat `renew` answered `deadline-exceeded` or `not-running`;
* - the request's `taken` reply did not arrive within {@link APP_POOL_SATELLITE_REQUEST_TIMEOUT_MS},
*   and the dispatcher is about to retry the run with a new attempt. It cancels the attempt id of
*   the request it gave up on, because the satellite may have taken it;
* - a reply or an event failed its contract, the reply echoed another attempt id, a result pointer
*   named another run, or the events stream ended before the terminal.
* On each of these the dispatcher copies both members from the request it sent: `attemptId` from
* `frame.attemptId`, `expiresAt` from the request's `expiresAt`.
*
* The adoption pass publishes the one cancel whose `expiresAt` is computed fresh (R-116). After an
* operator roll, a regional `running` row is not adopted: its pod is in the satellite cluster, which
* the home operator does not list, so the row is left to the reaper. Before the reaper retries it,
* the adoption pass cancels the row's attempt, in the background, and only for a region the current
* envelope enables. The original request's `expiresAt` is not persisted, so the cancel carries the
* home operator's clock plus {@link APP_POOL_SATELLITE_REQUEST_TIMEOUT_MS}, an instant later than
* the original.
*
* Parsed by the satellite executor. On every conforming message whose `expiresAt` its own clock has
* not passed, the satellite records the `attemptId` as cancelled, whether or not it holds the
* attempt. It keeps the record until the earlier of the cancel's `expiresAt` and its own clock at
* the cancel's arrival plus {@link APP_POOL_SATELLITE_REQUEST_TIMEOUT_MS}, so for at most one
* request timeout (R-116). A correct dispatcher sets a request's `expiresAt` one timeout ahead of
* its send, and the satellite refuses the request `expired` after that instant anyway (R-029), so a
* record kept one timeout past the cancel covers every queued copy of the request. The cap bounds a
* cancel whose `expiresAt` lies further out, up to `Number.MAX_SAFE_INTEGER`, which would otherwise
* pin its attempt id for the process's life. A repeated cancel of one id never shortens the record.
* A cancel whose `expiresAt` the clock has already passed records nothing: any request carrying
* that attempt is refused `expired`. Then:
* - An attempt the satellite holds for that subject's run: it deletes the attempt's pod, or, when a
*   pod create is in flight, deletes the pod once the create returns. It publishes no further event
*   for the attempt. This holds whatever the cancel's `expiresAt`, because the attempt already
*   started. The satellite registers an attempt under its `attemptId` when the request's snapshot
*   parses, before its reply and before its first pod create, so a cancel that arrives while the
*   pod is being created finds the attempt.
* - An attempt the satellite does not hold yet: the record alone acts. The request and the cancel
*   travel on two subjects with no delivery order between them, and a slow satellite, the likeliest
*   cause of a lost `taken` reply, can receive the cancel first. The satellite checks `expiresAt`
*   and the record after a request's snapshot parses and before it registers the attempt, as one
*   synchronous step with no await between the check and the registration. A request whose
*   `frame.attemptId` it recorded is answered `refused` with reason `attempt-failed`, and the
*   satellite creates nothing. So the stale request never runs a pod beside the retry's pod; the
*   retry carries a new `attemptId`, which no record names.
* - An attempt that has already ended: nothing to delete. A repeated cancel keeps the record at
*   the later of its two instants, so it is safe.
* The home dispatcher has already decided the attempt's outcome, and it drops any event or reply of
* that attempt that reaches it after the cancel. A message that fails the parse is dropped, and
* records, creates, deletes and publishes nothing.
*
* NATS core delivers at most once, so a cancel can be lost. Two backstops cover the two phases of
* the attempt, as the module doc states. Before the pod accepts, the satellite ends the wait at the
* snapshot's `deadlineMs` plus 1,020 s, the three pre-accept clocks in sum, counted from `taken`
* (R-116). A wait for capacity is bounded by that too. After the pod accepts, the snapshot's
* `deadlineMs`, counted from the accept, ends the handler. Each bound publishes `error` with reason
* `timeout` and deletes the pod, whether or not a cancel arrived.
*
* `.strict()`. `expiresAt` is a whole number from 1 to `Number.MAX_SAFE_INTEGER`, the request's own
* bound. A conforming object parses to itself; a missing `attemptId` or `expiresAt`, an id outside
* `workAttemptIdSchema`, an `expiresAt` outside that bound, or any other key is a `ZodError`. Pure.
*/
const appPoolSatelliteRunCancelSchema = z.object({
	attemptId: workAttemptIdSchema,
	expiresAt: satelliteExpiresAtSchema
}).strict();
/**
* One standing pool, resolved by the home operator, as it rides a desired-state slice: the wire
* twin of the operator's `ResolvedAppPool` without its `grant`, which rides the slice entry
* (2026-10-02-regional-pools-release-2 §2.1, DF-8).
*
* The satellite holds no catalog, no knob evaluator and no compute-class catalog, so the home
* operator sends every value resolved. Each member has the meaning and the null rule the
* `ResolvedAppPool` member of the same name has, so the satellite's slice consumer (WS-10) builds
* its view as `{ ...entry.view, grant: entry.grant }` and passes it to the operator's standing
* reconciler unchanged:
*
* - `appKey`: the app, in the label-value grammar, at most 63 characters.
* - `appVersion`, `imageFixtureVersion`: the active app version and the version of the fixture
*   whose image the pool runs, each 1 to 128 characters.
* - `name`: the pool's name, in `appPoolNameSchema`'s grammar.
* - `pool`: the declaration, the out-of-mesh arm only. An in-mesh pool is a parse error, so a
*   satellite builds no sidecar set, no ServiceAccount and no `AuthorizationPolicy` from a slice
*   (DF-8). `lifetime` must be `standing` (refine). The slice refines `region`.
* - `imageRef`: the digest-pinned pull reference the members image from, registry prefix included.
*   The envelope form with no registry host (`render-runner@sha256:<64 hex>`) is a parse error, so
*   a satellite never pulls from a registry the home did not name. The failure arm of the
*   operator's catalog (`resolveOne`'s catch) carries that envelope form, or no ref at all, so the
*   home publisher leaves such a view out of the slice: see
*   {@link appPoolSatelliteDesiredSliceSchema}.
* - `serviceAccountName`: the catalog's resolved account. The out-of-mesh arm ignores it; it rides
*   so the view is whole. A Kubernetes object name, or `''`. `''` parses so the view stays the
*   `ResolvedAppPool` twin; the catalog writes it only on its failure arm, which the home publisher
*   leaves out.
* - `nodeType`, `gpu`, `allocatable`: the resolved machine type, its accelerator and its
*   allocatable, each `null` while a resolution has not succeeded. `gpu` is non-null only for a GPU
*   class, or for a pool with `tiers` of which at least one is a GPU tier. For a pool with a GPU
*   tier, `gpu` names one GPU tier: its `count` is that tier's `gpuCount`, and its
*   `acceleratorType` is one of the tier's cloud types in the accelerator table. A resolved view
*   (`nodeType` non-null) of a pool that runs on GPU nodes only (a GPU class, or a pool whose every
*   tier is a GPU tier) carries a non-null `gpu`: a GPU node with no accelerator data gives a pod
*   with no device. A view with `nodeType: null` may carry `gpu: null`, as the local data plane's
*   `provider-local` view does (refines, 2026-10-24-pool-machine-generations R-003).
*   A resolved view with `gpu: null` of a pool with a CPU tier names a `nodeType` of the family of
*   one CPU tier of the pool, on that tier's cloud (refine, R-008). A resolved view whose `gpu`
*   names a GPU tier of the pool names a `nodeType` of a family that the accelerator table lists
*   for that tier's model on that tier's cloud (refine, D-10, D-26).
* - `groupId`: always `null`. An out-of-mesh view cannot name one group for every resident org;
*   each org's group rides the entry's `membership`.
* - `computeMax`, `computeDefault`: the admitted ceiling and the member size, `null` when unknown.
*   A standing member with no `computeDefault` is not sized; the reconciler logs it per pool.
* - `concurrency`, `warmFloor`, `maxNodes`: the post-knob, post-clamp figures, each a whole number
*   from 0 to the pool bound `APP_POOL_CONCURRENCY_MAX`, `APP_POOL_WARM_FLOOR_MAX` and
*   `APP_POOL_MAX_NODES_MAX`.
* - `placement`, `unschedulableReason`: the placement state and its reason, 1 to 512 characters or
*   `null`. The catalog's reason has no bound: a thrown message, or a placement refusal with its
*   detail. So the home publisher cuts it as the status path's `sliceReason` in `app-pool-status.ts`
*   does: `''` becomes `APP_POOL_UNSCHEDULABLE_REASON_UNSPECIFIED`, and a longer reason becomes its
*   first 512 characters. An uncut reason over 512 characters, or `''`, is a parse error.
*
* A pool that declares both `class` and `tiers`, or neither, is refused at `pool.tiers` (D-16). A GPU class
* on such a pool still counts as a GPU pool in the refines below. A GPU class, or a pool with a GPU
* tier, with `containerRuntime: true` is refused at `pool.containerRuntime`, the publish rules `pool-gpu-requires-default-sandbox` and
* `pool-gpu-tier-requires-default-sandbox` read at the wire, as
* {@link appPoolSatelliteRunRequestSchema} reads them. `.strict()` at every level. A conforming object parses to itself; anything else is a
* `ZodError`. Pure.
*/
const appPoolSatelliteSliceViewSchema = z.object({
	appKey: z.string().regex(APP_POOL_LABEL_VALUE_PATTERN).max(SATELLITE_RUN_APP_KEY_MAX_LENGTH),
	appVersion: appVersionStringSchema,
	name: appPoolNameSchema,
	pool: appPoolOutOfMeshSchema,
	imageRef: appPoolImagePullRefSchema,
	imageFixtureVersion: appVersionStringSchema,
	serviceAccountName: z.string().max(253).regex(/^(?:[a-z0-9](?:[-a-z0-9]*[a-z0-9])?)?$/),
	nodeType: z.string().regex(APP_POOL_LABEL_VALUE_PATTERN).max(APP_POOL_NODE_TYPE_MAX_LENGTH).nullable(),
	gpu: appPoolNodeGroupGpuSchema.nullable(),
	allocatable: computeVectorSchema.nullable(),
	groupId: z.null(),
	computeMax: computeVectorSchema.nullable(),
	computeDefault: computeVectorSchema.nullable(),
	concurrency: z.int().min(0).max(APP_POOL_CONCURRENCY_MAX),
	warmFloor: z.int().min(0).max(APP_POOL_WARM_FLOOR_MAX),
	maxNodes: z.int().min(0).max(APP_POOL_MAX_NODES_MAX),
	placement: z.enum(APP_POOL_PLACEMENT_STATES),
	unschedulableReason: z.string().min(1).max(512).nullable()
}).strict().refine((v) => v.pool.lifetime === "standing", {
	message: "a slice carries standing pools only; a per-attempt pool rides the run request",
	path: ["pool", "lifetime"]
}).refine((v) => poolHasOnePlacementForm(v.pool), {
	message: POOL_PLACEMENT_FORMS_MESSAGE,
	path: ["pool", "tiers"]
}).refine((v) => v.gpu === null || poolMayUseGpu(v.pool), {
	message: "gpu is refused for a pool with no GPU class and no GPU tier",
	path: ["gpu"]
}).refine((v) => v.gpu === null || poolDeclaresGpu(v.pool, v.gpu), {
	message: POOL_GPU_TIER_MESSAGE,
	path: ["gpu"]
}).refine((v) => v.nodeType === null || v.gpu !== null || poolMayUseCpu(v.pool), {
	message: "gpu is required on a resolved view of a pool that runs on GPU nodes only",
	path: ["gpu"]
}).refine((v) => v.nodeType === null || v.gpu !== null || poolDeclaresCpuNodeType(v.pool, v.nodeType), {
	message: POOL_CPU_TIER_MESSAGE,
	path: ["nodeType"]
}).refine((v) => v.nodeType === null || v.gpu === null || poolDeclaresGpuNodeType(v.pool, v.nodeType, v.gpu), {
	message: POOL_GPU_NODE_TYPE_MESSAGE,
	path: ["nodeType"]
}).refine((v) => !(poolMayUseGpu(v.pool) && v.pool.containerRuntime === true), {
	message: "a GPU class or a GPU tier runs on the default isolation arm only (pool-gpu-requires-default-sandbox)",
	path: ["pool", "containerRuntime"]
});
/**
* One org's node group for one standing pool: the compiler's membership entry
* `<appKey>/<pool>/<orgId>` to `groupId`, carried as a pair.
*
* `orgId` is the CP org id, a lowercase UUID; `groupId` is the regional group the home compiler
* derived with `appPoolNodeGroupId`, in the `apg-` grammar. `.strict()`. Pure.
*/
const appPoolSatelliteSliceMembershipSchema = z.object({
	orgId: appPoolOrgIdSchema,
	groupId: z.string().regex(APP_POOL_NODE_GROUP_ID_PATTERN)
}).strict();
/** Whether no value appears twice. */
function allDistinct(values) {
	return new Set(values).size === values.length;
}
/**
* Whether every membership entry of one slice pool names the regional node group of its own org:
* `appPoolNodeGroupId({ appKey: view.appKey, orgId, workload: 'out-of-mesh', nodeType:
* view.nodeType, region, diskSizeGb: view.pool.diskSizeGb, tier })`, the identity the home compiler
* hashes for an out-of-mesh group (2026-10-02-regional-pools-release-2 DF-6, chain D;
* 2026-10-24-pool-machine-generations D-26). `tier` is absent for a pool with `class`, and absent
* or the key of a declared tier of the view's machine for a pool with `tiers`: see
* {@link groupIdNamesOrgGroup}.
*
* Read by the slice's membership refine. `region` is the slice's region, so a group of another
* region never binds. Output per input class:
* - an empty membership: `true`, because no entry names a group;
* - a view with `nodeType: null`: `false` for any entry, because no group exists for a pool with no
*   resolved machine type;
* - a view of a pool with `tiers` of which no declared tier names the view's machine: `true`. The
*   view's CPU tier refine or GPU node refine at `nodeType`, or a GPU refine at `gpu`, refuses that
*   view, and with it the slice;
* - a `region` outside `APP_POOL_REGION_PATTERN`, or a `view.pool.diskSizeGb` outside the node
*   group's 10 to 4096 whole-number bounds: `false` for any entry, because
*   {@link derivedOutOfMeshGroupId} answers `null` for these two values;
* - otherwise `true` exactly when each `groupId` equals the derived id of its `orgId`, tierless or
*   for one tier that names the view's machine. So on a pool with `tiers` the id of a tier the pool does not declare, or of
*   a declared tier of another machine, is refused.
*
* Pure. It never throws.
*/
function membershipBindsOrgGroups(entry, region) {
	if (entry.membership.length === 0) return true;
	const { appKey, nodeType, gpu, pool } = entry.view;
	if (nodeType === null) return false;
	return entry.membership.every((m) => groupIdNamesOrgGroup(m.groupId, {
		appKey,
		orgId: m.orgId,
		nodeType,
		region,
		diskSizeGb: pool.diskSizeGb
	}, pool, gpu ?? void 0));
}
/**
* One pool of a desired-state slice: `{ view, membership, residentOrgIds, grant }`
* (2026-10-02-regional-pools-release-2 §2.1).
*
* - `view`: the resolved pool, see {@link appPoolSatelliteSliceViewSchema}.
* - `membership`: the org-to-group pairs of this pool, at most `APP_POOL_NODE_GROUP_MAX` (100).
*   Each org once and each group once (refines): an out-of-mesh group hashes its org, so two orgs
*   on one group would put two tenants on one set of nodes (chain D). Every org is an entry of
*   `residentOrgIds` (refine). The slice binds each `groupId` to its org: see
*   {@link appPoolSatelliteDesiredSliceSchema}.
* - `residentOrgIds`: the orgs a member Deployment is wanted for, each once, at most
*   `APP_POOL_NODE_GROUP_MAX`. The home operator sends the resident orgs it compiled a regional
*   group for: an out-of-mesh member with no group cannot render, and a data plane compiles at most
*   100 groups, so no further org could run a member (R-008). A resident org with no membership entry is
*   legal, and the satellite logs that org's member as failed, as the home reconciler does.
* - `grant`: the CP's grant for the pool. It must not exceed the declaration: the refine runs
*   `assertAppPoolGrantConsistent`, so a widened grant is refused at the satellite as well as at
*   the CP. It is the value the run request carries per attempt today, so the slice adds no trust.
*
* `.strict()`. A conforming object parses to itself; anything else is a `ZodError`. Pure.
*/
const appPoolSatelliteSlicePoolSchema = z.object({
	view: appPoolSatelliteSliceViewSchema,
	membership: z.array(appPoolSatelliteSliceMembershipSchema).max(100),
	residentOrgIds: z.array(appPoolOrgIdSchema).max(100),
	grant: AppPoolGrantSchema
}).strict().refine((p) => allDistinct(p.residentOrgIds), {
	message: "each resident org appears once",
	path: ["residentOrgIds"]
}).refine((p) => allDistinct(p.membership.map((m) => m.orgId)), {
	message: "each org has one membership entry",
	path: ["membership"]
}).refine((p) => allDistinct(p.membership.map((m) => m.groupId)), {
	message: "two orgs never share a node group (chain D)",
	path: ["membership"]
}).refine((p) => p.membership.every((m) => p.residentOrgIds.includes(m.orgId)), {
	message: "every membership org is a resident org",
	path: ["membership"]
}).refine((p) => grantFitsPool(p.view.pool, p.grant), {
	message: "grant exceeds the pool declaration",
	path: ["grant"]
});
/**
* The desired state of one region's standing pools, as the home operator publishes it on
* {@link appPoolSatelliteDesiredSubject} (2026-10-02-regional-pools-release-2 D-7, DF-7, DF-8).
*
* Published by the home operator's slice publisher (WS-9) once per enabled region per reconcile,
* whole: every standing out-of-mesh pool whose `region` is the slice's and whose resolution did
* not throw, and `pools: []` for a region with none, so a satellite retires members the home no
* longer asks for. Parsed by the
* satellite's slice consumer (WS-10), which drops a slice that fails the parse, a slice whose
* `region` is not its own `SATELLITE_REGION`, and a slice whose `generation` is not greater than
* the one it holds, and logs each drop. The satellite then runs the operator's standing reconciler
* over the views, the membership and the resident orgs of the slice it holds. Members:
*
* - `generation`: a whole number from 1 to `Number.MAX_SAFE_INTEGER`. The home operator raises it
*   on every publish: the greater of the epoch-millisecond clock and the last figure it sent plus
*   one (R-005, R-034). After a clock fault it resets to the clock plus the satellite's future
*   bound. The figure says nothing about the content: two consecutive slices with the same content
*   carry two figures, and a restarted home operator publishes a greater figure than before. The
*   satellite applies a slice whose figure is greater than the one it holds and drops the rest.
* - `region`: the region the slice is for, in `APP_POOL_REGION_PATTERN`'s grammar. Every view's
*   `pool.region` equals it (refine).
* - `pools`: at most `APP_POOL_REPORTED_STATUS_MAX` (200), the most declared pools one status report
*   carries, each `(view.appKey, view.name)` once (refine). Across the pools, at most
*   `APP_POOL_SATELLITE_MEMBERS_MAX` (2,000) membership entries (refine), the most member sets one
*   satellite runs.
*
* Membership binding (chain D): every membership entry of every pool names the group
* `appPoolNodeGroupId({ appKey: view.appKey, orgId, workload: 'out-of-mesh', nodeType:
* view.nodeType, region, diskSizeGb: view.pool.diskSizeGb, tier })` with the slice's `region`, the
* identity the home compiler hashes (DF-6). `tier` is absent for a pool with `class`, and absent
* or the key of a declared tier of the view's machine for a pool with `tiers`
* (2026-10-24-pool-machine-generations D-26). So a slice cannot place org A on org B's group, on a
* group of another pool, of another region, of another disk size or of an undeclared tier. A view whose `nodeType` is
* `null` has no group, so its membership is empty. A refused pool is named at
* `pools.<index>.membership`. The satellite never computes a group id itself; it rejects the slice.
*
* Producer rule: one refused view refuses the whole slice. zod refuses the slice when any pool
* fails its parse, and the satellite then keeps the slice it holds, so every other standing pool
* of the region freezes at that content. The home publisher therefore sends only views that parse
* (2026-10-02-regional-pools-release-2 WS-2 architecture-7):
* 1. It leaves out a view from the catalog's failure arm (`resolveOne`'s catch, `serviceAccountName`
*    `''`). That view's `imageRef` is the envelope form with no registry host, or absent, so the
*    view schema refuses it. The satellite retires the pool's members, as it would for the failure
*    view itself: that view's `nodeType` is `null`, so its membership must be empty.
* 2. It cuts every `unschedulableReason` as `sliceReason` does: `''` becomes
*    `APP_POOL_UNSCHEDULABLE_REASON_UNSPECIFIED`, and a reason over 512 characters becomes its
*    first 512 characters.
*
* Size: a slice can exceed the NATS server's `max_payload` (1 MiB by default) at its count bounds,
* so the home publisher checks the encoded slice against that limit before it publishes, logs the
* refusal and publishes nothing for the region. No member is a credential.
*
* `.strict()` at every level. A conforming object parses to itself; anything else is a `ZodError`.
* Pure.
*/
const appPoolSatelliteDesiredSliceSchema = z.object({
	generation: z.int().positive(),
	region: appPoolRegionSchema,
	pools: z.array(appPoolSatelliteSlicePoolSchema).max(200)
}).strict().refine((s) => s.pools.every((p) => p.view.pool.region === s.region), {
	message: "every pool.region must equal the slice region",
	path: ["pools"]
}).refine((s) => allDistinct(s.pools.map((p) => JSON.stringify([p.view.appKey, p.view.name]))), {
	message: "each (appKey, name) pool appears once",
	path: ["pools"]
}).refine((s) => s.pools.reduce((sum, p) => sum + p.membership.length, 0) <= APP_POOL_SATELLITE_MEMBERS_MAX, {
	message: `a slice carries at most ${APP_POOL_SATELLITE_MEMBERS_MAX} membership entries`,
	path: ["pools"]
}).superRefine((s, ctx) => {
	s.pools.forEach((p, index) => {
		if (!membershipBindsOrgGroups(p, s.region)) ctx.addIssue({
			code: "custom",
			message: "each membership groupId is the regional node group of its own org (chain D)",
			path: [
				"pools",
				index,
				"membership"
			]
		});
	});
});
//#endregion
export { APP_POOL_SATELLITE_REQUEST_TIMEOUT_MS, appPoolSatelliteDesiredSliceSchema, appPoolSatelliteDesiredSubject, appPoolSatelliteRunCancelSchema, appPoolSatelliteRunCancelSubject, appPoolSatelliteRunEventSchema, appPoolSatelliteRunEventsSubject, appPoolSatelliteRunReplySchema, appPoolSatelliteRunRequestSchema, appPoolSatelliteRunSubject, appPoolSatelliteStatusSubject, homeSatelliteNatsGrants, satelliteNatsGrants };

//# sourceMappingURL=app-pool-subjects.js.map